import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  Combobox,
  Grid,
  H3,
  P,
  TagInput,
} from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import startCase from 'lodash/startCase';
import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';
import PropTypes from 'prop-types';
import Asterisk from '@decisiv/iconix/lib/components/Asterisk';
import SectionRow from '../../components/SectionRow';
import filterOptions from '../../utils/filterOptions';

export default function Trigger({
  handleForm,
  applicationEventsItems,
  triggerTypeApplicationEvent,
  triggerTypeHttpRequest,
  editMode = false,
  integration,
}) {
  const [inputValue, setInputValue] = useState('');

  const [applicationEventsValue, setApplicationEventsValue] = useState([]);
  const [customEventsTags, setCustomEventsTags] = useState([]);

  const TRIGGER_TYPE_APPLICATION_EVENT = 'application_event';
  const TRIGGER_TYPE_HTTP_API_REQUEST = 'http_api_request';
  const HTTP_API_REQUEST_LABEL = 'HTTP API Request';

  const handleApplicationEvents = () => {
    /* istanbul ignore next */
    const updateApplicationEvents = (integration?.attributes?.events || [])
      .filter((event) =>
        applicationEventsItems.some((item) => item.id === event),
      )
      .map((applicationEvent) => ({
        id: applicationEvent,
        label: applicationEvent,
        value: applicationEvent,
      }));
    setApplicationEventsValue(updateApplicationEvents);
    setInputValue('');
  };

  const handleCustomApplicationEvents = () => {
    const updateCustomEvents = (integration?.attributes?.events || []).filter(
      (event) => !applicationEventsItems.some((item) => item.id === event),
    );
    setCustomEventsTags(updateCustomEvents);
  };

  useEffect(() => {
    handleApplicationEvents();
    handleCustomApplicationEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration?.attributes?.events, applicationEventsItems]);

  return (
    <SectionRow>
      <Grid.Column padding={0} span="3">
        <H3>
          <Trans>Trigger</Trans>
        </H3>
      </Grid.Column>
      <Grid.Column padding={0}>
        <P shade={1} size="small">
          <Trans>Define how the integration is invoked.</Trans>
          <Asterisk
            color={toColorString(color.status.information.medium)}
            css={{ verticalAlign: 'middle' }}
            size="small"
          />{' '}
        </P>

        <div style={{ marginTop: '0.5rem' }}>
          <Checkbox
            name={TRIGGER_TYPE_APPLICATION_EVENT}
            id={TRIGGER_TYPE_APPLICATION_EVENT}
            label={startCase(TRIGGER_TYPE_APPLICATION_EVENT)}
            value={TRIGGER_TYPE_APPLICATION_EVENT}
            defaultChecked={
              editMode
                ? integration?.attributes.trigger_type.includes(
                    TRIGGER_TYPE_APPLICATION_EVENT,
                  )
                : null
            }
            onChange={(e) => {
              handleForm({ event: e, field: 'trigger_type' });
            }}
          />
        </div>

        {(triggerTypeApplicationEvent ||
          integration?.attributes?.trigger_type?.includes(
            'application_event',
          )) && (
          <>
            <Combobox
              required={customEventsTags.length === 0}
              disabled={customEventsTags.length > 0}
              multiple
              label={t`Predefined Application Events`}
              options={filterOptions(applicationEventsItems, inputValue)}
              value={applicationEventsValue}
              style={{ marginTop: '0.5rem', marginLeft: '2.050rem' }}
              onChangeInputValue={(e) => setInputValue(e ? e.target.value : '')}
              onChangeValue={(selectedId, options) => {
                handleForm({
                  selectedId,
                  options,
                  field: 'events',
                });
                handleApplicationEvents();
              }}
            />
            <div style={{ marginTop: '0.5rem', marginLeft: '2.050rem' }}>
              <TagInput
                label={t`Custom Application Events`}
                required={applicationEventsValue.length === 0}
                disabled={applicationEventsValue.length > 0}
                helpMessage={t`Press enter to add custom application events`}
                onChange={(newCustomEvents) => {
                  handleForm({
                    customValues: newCustomEvents,
                    field: 'custom_events',
                  });
                  handleCustomApplicationEvents();
                }}
                tags={customEventsTags}
              />
            </div>
          </>
        )}

        <div style={{ marginTop: '0.5rem' }}>
          <Checkbox
            name={TRIGGER_TYPE_HTTP_API_REQUEST}
            id={TRIGGER_TYPE_HTTP_API_REQUEST}
            label={HTTP_API_REQUEST_LABEL}
            value={TRIGGER_TYPE_HTTP_API_REQUEST}
            style={{ marginTop: '0.5rem' }}
            defaultChecked={
              editMode
                ? integration?.attributes.trigger_type.includes(
                    TRIGGER_TYPE_HTTP_API_REQUEST,
                  )
                : null
            }
            onChange={(e) => {
              handleForm({ event: e, field: 'trigger_type' });
            }}
          />
        </div>

        {(triggerTypeHttpRequest ||
          integration?.attributes?.trigger_type?.includes(
            'http_api_request',
          )) && (
          <div
            style={{
              marginTop: '0.65rem',
              marginLeft: '2rem',
            }}
          >
            <a
              href={`http://integration-framework-swagger-document-bucket-staging.s3-website-${process.env.REACT_APP_AWS_REGION}.amazonaws.com/`}
              target="_blank"
              rel="noreferrer"
              style={{
                color: toColorString(color.interaction.pacificOcean),
              }}
            >
              <Trans>API Documentation</Trans>
            </a>
          </div>
        )}
      </Grid.Column>
    </SectionRow>
  );
}

Trigger.propTypes = {
  handleForm: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  applicationEventsItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  triggerTypeApplicationEvent: PropTypes.bool.isRequired,
  triggerTypeHttpRequest: PropTypes.bool.isRequired,
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  editMode: PropTypes.bool,
};
